/* eslint-disable import/prefer-default-export */
import { useContext } from 'react'
import Translations from '@helpers/translations'
import {
  isAllowedProductCategoryBySku,
  returnsAnalytics,
  returnsClickEvent
} from '@components/returns/helpers'

import { RETURN_REASON_CODES } from '@components/returns/consts'
import Dropdown from '@components/ui/formElements/Dropdown'
import ExchangeNotification from '../../shared/ExchangeNotification'
import { Button } from '@components/ui/Button'
import { productSizesMatchCustomerFit } from '../../shared/ExchangeNotification/index'
import ReturnsContext from '../../../context'
import { Values, ReasonListItem, Selection } from '@components/returns/types'
import FormWrapper from '../../shared/FormWrapper'
import { PartialState, ValuesState } from '../..'
import styles from '../../shared/styles.module.scss'
import { objectOmit } from '@helpers/utils'
import { ReturnsProduct } from '@api/returns/types'

export type ReasonStepProps = {
  selectedProduct: ReturnsProduct
  values: Values
  reasonList: ReasonListItem[]
  setSelection: (
    selection:
      | Selection
      | {
          sizes: Values['sizes'] & { available?: boolean }
        }
  ) => void
  onUpdate: () => void
  isLastStep: boolean
}

export function ReasonStep({
  selectedProduct,
  values,
  reasonList,
  setSelection,
  onUpdate,
  isLastStep
}: ReasonStepProps) {
  const { customerFit, setStoredSavedFit } = useContext(ReturnsContext)

  const matchesCustomerFit = productSizesMatchCustomerFit({
    sizes: selectedProduct.sizes,
    customerFit: customerFit
  })

  const selectedReason = values.reason
  const buttonText = isLastStep
    ? Translations.localise('pages.returns.done')
    : 'Next'

  const handleUpdate = () => {
    returnsClickEvent({
      label: buttonText
    })
    onUpdate()
  }

  const handleOnMainReasonSelection = (
    selection: Record<string, string | number>
  ) => {
    const reason = Number(selection.reason)
    setSelection({
      reason,
      reason_detail: []
    })
    const reasonLabel =
      (reasonList.find(reasonListItem => reasonListItem.value === reason) || {})
        .label || ''
    returnsAnalytics('Return Reason Selected', {
      color: selectedProduct.color,
      name: selectedProduct.product_type,
      product_id: selectedProduct.product_id,
      return_type: values.method,
      return_reason: reasonLabel
    })
  }

  type HandleSelectingSizesProperties = {
    sizes?: PartialState<ValuesState['sizes']>
    step?: number
  }

  const handleSelectingSizes = ({ sizes }: HandleSelectingSizesProperties) => {
    if (sizes) {
      setSelection({ sizes })
      const updatedSizes = objectOmit(sizes, ['taper', 'available'])
      setStoredSavedFit(updatedSizes)
      onUpdate()
    }
  }

  const matchesAllowedProductCategory = selectedProduct?.sku
    ? isAllowedProductCategoryBySku(selectedProduct.sku)
    : true

  const shouldShowRefundNotification =
    !matchesCustomerFit &&
    matchesAllowedProductCategory &&
    (selectedReason === RETURN_REASON_CODES.FIT_NOT_RIGHT ||
      selectedReason === RETURN_REASON_CODES.LENGTH_NOT_RIGHT)

  return (
    <>
      <FormWrapper title="Reason for exchange">
        <Dropdown
          defaultValue="Please Select"
          placeholder="Please Select"
          classes={`${styles.returnsForm__dropdown} dropdown--wide dropdown__returns`}
          selectedClasses={`dropdown--capitalize dropdown--left ${
            !selectedReason && 'dropdown--grey'
          }`}
          list={reasonList}
          selection={selectedReason}
          setSelection={handleOnMainReasonSelection}
          optionClasses="list__option--capitalize list__option--left list__option--returns"
          id="reason"
        />
        {shouldShowRefundNotification && (
          <ExchangeNotification
            type="exchange"
            reason={selectedReason}
            productSizes={selectedProduct?.sizes}
            productType={selectedProduct.product_type}
            onExchange={handleSelectingSizes}
          />
        )}
      </FormWrapper>
      <Button
        disabled={!selectedReason}
        classes={styles.returnsForm__button}
        variant="primary"
        fullWidth="always"
        onClick={handleUpdate}
      >
        {buttonText}
      </Button>
    </>
  )
}
